import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";
import {
  actions as detailTransactionActions,
  detailTransactionSelector,
} from "reducers/detailTransaction";
import { postDnideliveredRequestType } from "sagas/types/detailTransactions";
import { FilterTransactionsType } from "sagas/types/transactions";
import { AppDispatch, RootState } from "store";
import "./DniFinish.scss";
import {
  actions as transactionActions,
  transactionsSelector,
} from "reducers/transactions";
import "../i18n";
import { sessionSelector } from "reducers/session";
import { getProofOfDeliveryRequiredList } from "../proofOfDeliveryRequired";
import {
  DniFinishContainerPropsType,
  DniFinishFormValuesType,
  deliveryProofFileType,
} from "./types";
import { DeliveryProofFileResponseType } from "sagas/types/detailTransactions";
import { DEFAULT_COUNTRY } from "hooks/useSession";
import { DniFinish } from "./DniFinish";
import useBasicData from "hooks/useBasicData";

const DniFinishContainer: React.FC<DniFinishContainerPropsType> = (
  props
): JSX.Element => {
  const { countryCode, detailTransaction } = props;
  const { relationships } = useBasicData(["relationships"]);
  const [photoFile, setPhotoFile] = useState<File | null>(null);
  const [signFile, setSignFile] = useState<File | null>(null);
  const [photoKey, setPhotoKey] = useState<string | null>(null);
  const [signKey, setSignKey] = useState<string | null>(null);
  const deliveryValidation = detailTransaction?.transaction?.deliveryValidation;
  const showSerialNumberInput = deliveryValidation?.type === "SERIAL_NUMBER";
  const expectedSerialCode = deliveryValidation?.codes ?? undefined;

  const proofOfDeliveryRequired = useMemo(
    () => getProofOfDeliveryRequiredList(detailTransaction),
    [detailTransaction]
  );

  const destination = detailTransaction?.destination;
  const dest = destination as Record<string, unknown> | undefined;
  const latitude =
    destination?.latitude ??
    (typeof dest?.latitude === "number" ? dest.latitude : undefined) ??
    (typeof dest?.lat === "number" ? dest.lat : undefined) ??
    0;
  const longitude =
    destination?.longitude ??
    (typeof dest?.longitude === "number" ? dest.longitude : undefined) ??
    (typeof dest?.lng === "number" ? dest.lng : undefined) ??
    0;

  const finishTransactions = useCallback(
    (
      values: DniFinishFormValuesType,
      keys?: DeliveryProofFileResponseType
    ) => {
      const params: postDnideliveredRequestType = {
        name: values.name ?? null,
        lastName: values.lastName ?? null,
        identificationNumber:
          countryCode === "MX"
            ? (values.identificationNumber ?? null)?.toUpperCase()
            : (values.identificationNumber ?? null),
        relationship: values.relationship ?? null,
        photo: keys?.photo ?? null,
        sign: keys?.sign ?? null,
        finishByAdmin: true,
      };
      if (latitude !== 0 || longitude !== 0) {
        params.latitude = latitude;
        params.longitude = longitude;
      }
      if (values.serialNumber != null && values.serialNumber !== null) {
        params.serialNumber = values.serialNumber;
      }
      props.getDetailTransactionDniDeliveredRequest({
        params,
        id: detailTransaction.transaction.id.toString(),
        callBack: () => {
          props.getTransactions({
            ...props.filters,
            offset: 0,
          });
        },
        countryCode,
        latitude,
        longitude,
      });
    },
    [countryCode, detailTransaction.transaction.id, latitude, longitude, props]
  );

  const onPhotoUpload = useCallback(
    async (file: File) => {
      setPhotoKey(null);
      const formData = new FormData();
      const buffer = await file.arrayBuffer();
      const blob = new Blob([buffer], { type: file.type || "image/jpeg" });
      const name = file.name || "photo.jpg";
      // Mismo formato que el curl: multipart con un solo campo; nombre "file" y filename
      formData.append("file", blob, name);
      props.postDeliveryProofFile({
        id: detailTransaction.transaction.id,
        file: formData,
        xType: "photo",
        callBack: (keys) => setPhotoKey(keys.photo ?? null),
      });
    },
    [detailTransaction.transaction.id, props]
  );

  const onSignUpload = useCallback(
    async (file: File) => {
      setSignKey(null);
      const formData = new FormData();
      const buffer = await file.arrayBuffer();
      const blob = new Blob([buffer], { type: file.type || "image/png" });
      const name = file.name || "signature.png";
      // Un solo campo "file", filename signature.png (como en el curl que funciona)
      formData.append("file", blob, name);
      props.postDeliveryProofFile({
        id: detailTransaction.transaction.id,
        file: formData,
        xType: "sign",
        callBack: (keys) => setSignKey(keys.sign ?? null),
      });
    },
    [detailTransaction.transaction.id, props]
  );

  const handleSubmit = useCallback(
    (values: DniFinishFormValuesType) => {
      finishTransactions(values, {
        photo: photoKey ?? undefined,
        sign: signKey ?? undefined,
      });
    },
    [finishTransactions, photoKey, signKey]
  );

  const setPhotoFileAndClearKey = useCallback((file: File | null) => {
    setPhotoFile(file);
    if (file == null) setPhotoKey(null);
  }, []);
  const setSignFileAndClearKey = useCallback((file: File | null) => {
    setSignFile(file);
    if (file == null) setSignKey(null);
  }, []);

  return (
    <DniFinish
      testId="modal-finish-transaction"
      {...props}
      photoFile={photoFile}
      signFile={signFile}
      photoKey={photoKey}
      signKey={signKey}
      setPhotoFile={setPhotoFileAndClearKey}
      setSignFile={setSignFileAndClearKey}
      onPhotoUpload={onPhotoUpload}
      onSignUpload={onSignUpload}
      handleSubmit={handleSubmit}
      relationships={relationships}
      proofOfDeliveryRequired={proofOfDeliveryRequired}
      showSerialNumberInput={showSerialNumberInput}
      expectedSerialCode={expectedSerialCode ?? undefined}
    />
  );
};

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  getTransactions: (params: FilterTransactionsType) => {
    dispatch(transactionActions.getTransactionsRequest(params));
  },
  getDetailTransactionDniDeliveredRequest: (payload: {
    params: postDnideliveredRequestType;
    id: string;
    callBack: Function;
    countryCode?: string;
    latitude?: number;
    longitude?: number;
  }) => {
    dispatch(
      detailTransactionActions.getDetailTransactionDniDeliveredRequest(
        payload
      )
    );
  },
  postDeliveryProofFile: (params: deliveryProofFileType) => {
    dispatch(detailTransactionActions.postDeliveryProofFileRequest(params));
  },
});

const mapStateToProps = (state: RootState) => ({
  detailTransaction: detailTransactionSelector(state).detailTransaction,
  filters: transactionsSelector(state).filters,
  filtersExtra: {
    limit: transactionsSelector(state).limit,
    offset: transactionsSelector(state).offset,
  },
  countryCode: sessionSelector(state).countryCode || DEFAULT_COUNTRY,
});
export default connect(mapStateToProps, mapDispatchToProps)(DniFinishContainer);
